import { useState, useRef } from 'react';
import { useUploadJobImage, useDeleteJobImage } from '../../hooks/unified/useJobsQueries';
import { useToast } from '../../contexts/ToastContext';
import { CompanyAssetLibrary } from './CompanyAssetLibrary';

interface JobImageUploadProps {
  type: 'logo' | 'job-image';
  currentImageUrl?: string;
  onImageUploaded: (imageUrl: string) => void;
  onImageDeleted?: () => void;
  showAssetLibrary?: boolean;
  companyId?: string;
  hideLabel?: boolean; // Hide the internal label and library button
}

export function JobImageUpload({
  type,
  currentImageUrl,
  onImageUploaded,
  onImageDeleted,
  showAssetLibrary = true,
  companyId,
  hideLabel = false,
}: JobImageUploadProps) {
  const [dragActive, setDragActive] = useState(false);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [isAssetLibraryOpen, setIsAssetLibraryOpen] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { showSuccess, showError } = useToast();

  const uploadMutation = useUploadJobImage();
  const deleteMutation = useDeleteJobImage();

  const isUploading = uploadMutation.isPending;

  // Get recommended dimensions based on type
  const getRecommendedDimensions = () => {
    if (type === 'logo') {
      return '200x200px (square)';
    }
    return '1200x630px (landscape, Open Graph ratio)';
  };

  // Get display label based on type
  const getLabel = () => {
    if (type === 'logo') {
      return 'Company Logo';
    }
    return 'Job Image';
  };

  // Get description based on type
  const getDescription = () => {
    if (type === 'logo') {
      return 'Upload your company logo. This will appear in the job listing header and company profile.';
    }
    return 'Upload a hero image for the job posting. This will be displayed prominently at the top of the job details page.';
  };

  // Validate file type
  const validateFileType = (file: File): boolean => {
    const validTypes = ['image/png', 'image/jpeg', 'image/jpg', 'image/webp'];
    return validTypes.includes(file.type);
  };

  // Validate file size (max 5MB)
  const validateFileSize = (file: File): boolean => {
    const maxSize = 5 * 1024 * 1024; // 5MB
    return file.size <= maxSize;
  };

  // Handle file selection
  const handleFileSelect = (file: File) => {
    // Validate file type
    if (!validateFileType(file)) {
      showError('Invalid file type', 'Please upload a PNG, JPG, or WebP image');
      return;
    }

    // Validate file size
    if (!validateFileSize(file)) {
      showError('File too large', 'Maximum file size is 5MB');
      return;
    }

    // Set selected file and create preview
    setSelectedFile(file);
    const reader = new FileReader();
    reader.onloadend = () => {
      setPreviewUrl(reader.result as string);
    };
    reader.readAsDataURL(file);
  };

  // Handle drag events
  const handleDrag = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  };

  // Handle drop event
  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);

    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handleFileSelect(e.dataTransfer.files[0]);
    }
  };

  // Handle file input change
  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    e.preventDefault();
    if (e.target.files && e.target.files[0]) {
      handleFileSelect(e.target.files[0]);
    }
  };

  // Handle upload
  const handleUpload = async () => {
    if (!selectedFile) return;

    try {
      const response = await uploadMutation.mutateAsync({
        file: selectedFile,
        type,
      });

      showSuccess(`${getLabel()} uploaded successfully`);
      onImageUploaded(response.image_url);

      // Clear preview and selected file
      setPreviewUrl(null);
      setSelectedFile(null);
    } catch (error) {
      showError('Upload failed', `Failed to upload ${getLabel().toLowerCase()}`);
      console.error('Image upload error:', error);
    }
  };

  // Handle delete
  const handleDelete = async () => {
    if (!currentImageUrl) return;

    if (!confirm(`Are you sure you want to delete this ${getLabel().toLowerCase()}?`)) {
      return;
    }

    try {
      // Extract filename from URL
      const filename = currentImageUrl.split('/').pop();
      if (!filename) {
        throw new Error('Invalid image URL');
      }

      await deleteMutation.mutateAsync(filename);
      showSuccess(`${getLabel()} deleted successfully`);

      if (onImageDeleted) {
        onImageDeleted();
      }
    } catch (error) {
      showError('Delete failed', `Failed to delete ${getLabel().toLowerCase()}`);
      console.error('Image delete error:', error);
    }
  };

  // Cancel preview
  const handleCancelPreview = () => {
    setPreviewUrl(null);
    setSelectedFile(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  // Handle asset selection from library
  const handleAssetSelected = (assetUrl: string, assetType: 'logo' | 'image') => {
    // Validate that the asset type matches the expected type
    const expectedAssetType = type === 'logo' ? 'logo' : 'image';
    if (assetType !== expectedAssetType) {
      showError('Invalid asset type', `Please select a ${expectedAssetType} asset`);
      return;
    }

    onImageUploaded(assetUrl);
    setIsAssetLibraryOpen(false);
  };

  // Handle opening asset library
  const handleOpenAssetLibrary = () => {
    if (!companyId) {
      showError('No company selected', 'Please select a company first to browse its assets');
      return;
    }
    setIsAssetLibraryOpen(true);
  };

  const displayImage = previewUrl || currentImageUrl;

  return (
    <>
      <div className="space-y-4">
        <div>
          {!hideLabel && (
            <div className="flex items-center justify-between mb-2">
              <label className="block text-sm font-medium text-gray-700">
                {getLabel()}
              </label>
              {showAssetLibrary && (
                <button
                  type="button"
                  onClick={() => setIsAssetLibraryOpen(true)}
                  className="text-sm text-primary-600 hover:text-primary-700 font-medium"
                >
                  Choose from Library
                </button>
              )}
            </div>
          )}
          <p className="text-xs text-gray-500 mb-3">{getDescription()}</p>

          {/* Display current or preview image */}
          {displayImage && (
            <div className="mb-4">
              <div className="relative inline-block">
                <img
                  src={displayImage}
                  alt={getLabel()}
                  className={`border-2 border-gray-300 rounded-lg ${
                    type === 'logo' 
                      ? 'h-40 w-40 object-contain bg-white p-2' 
                      : 'max-h-80 w-auto max-w-full object-cover'
                  }`}
                />
                {previewUrl && (
                  <div className="absolute top-2 right-2">
                    <span className="inline-flex items-center px-2 py-1 rounded text-xs font-medium bg-yellow-100 text-yellow-800">
                      Preview
                    </span>
                  </div>
                )}
              </div>
              <div className="mt-2 text-xs text-gray-500">
                {type === 'logo' 
                  ? 'Square format recommended (200x200px)' 
                  : 'Landscape format recommended (1200x630px)'}
              </div>
              <div className="mt-3 flex items-center space-x-2">
                {previewUrl ? (
                  <>
                    <button
                      type="button"
                      onClick={handleUpload}
                      disabled={isUploading}
                      className="inline-flex items-center px-4 py-2 text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 rounded-md disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {isUploading ? (
                        <>
                          <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                          </svg>
                          Uploading...
                        </>
                      ) : (
                        'Upload Image'
                      )}
                    </button>
                    <button
                      type="button"
                      onClick={handleCancelPreview}
                      disabled={isUploading}
                      className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 hover:bg-gray-50 rounded-md disabled:opacity-50"
                    >
                      Cancel
                    </button>
                  </>
                ) : (
                  <>
                    <button
                      type="button"
                      onClick={handleDelete}
                      disabled={deleteMutation.isPending}
                      className="inline-flex items-center px-4 py-2 text-sm font-medium text-red-600 bg-white border border-red-300 hover:bg-red-50 rounded-md disabled:opacity-50"
                    >
                      {deleteMutation.isPending ? 'Deleting...' : 'Remove Image'}
                    </button>
                    {showAssetLibrary && companyId && (
                      <button
                        type="button"
                        onClick={handleOpenAssetLibrary}
                        className="inline-flex items-center px-4 py-2 text-sm font-medium text-primary-600 bg-white border border-primary-300 hover:bg-primary-50 rounded-md"
                      >
                        Replace from Library
                      </button>
                    )}
                  </>
                )}
              </div>
            </div>
          )}

          {/* Upload Area */}
          {!displayImage && (
            <div
              className={`relative border-2 border-dashed rounded-lg p-8 transition-colors ${
                dragActive
                  ? 'border-primary-500 bg-primary-50'
                  : 'border-gray-300 hover:border-gray-400'
              }`}
              onDragEnter={handleDrag}
              onDragLeave={handleDrag}
              onDragOver={handleDrag}
              onDrop={handleDrop}
            >
              <input
                ref={fileInputRef}
                type="file"
                className="hidden"
                accept="image/png,image/jpeg,image/jpg,image/webp"
                onChange={handleChange}
                disabled={isUploading}
              />

              <div className="text-center">
                <svg
                  className="mx-auto h-12 w-12 text-gray-400"
                  stroke="currentColor"
                  fill="none"
                  viewBox="0 0 48 48"
                >
                  <path
                    d="M28 8H12a4 4 0 00-4 4v20m32-12v8m0 0v8a4 4 0 01-4 4H12a4 4 0 01-4-4v-4m32-4l-3.172-3.172a4 4 0 00-5.656 0L28 28M8 32l9.172-9.172a4 4 0 015.656 0L28 28m0 0l4 4m4-24h8m-4-4v8m-12 4h.02"
                    strokeWidth={2}
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
                <div className="mt-4 space-y-2">
                  <div>
                    <button
                      type="button"
                      onClick={() => fileInputRef.current?.click()}
                      disabled={isUploading}
                      className="text-primary-600 hover:text-primary-700 font-medium disabled:opacity-50"
                    >
                      {isUploading ? 'Uploading...' : `Upload ${getLabel()}`}
                    </button>
                    <p className="text-xs text-gray-500 mt-1">or drag and drop</p>
                  </div>
                  {showAssetLibrary && companyId && (
                    <div>
                      <button
                        type="button"
                        onClick={handleOpenAssetLibrary}
                        className="text-sm text-primary-600 hover:text-primary-700 font-medium"
                      >
                        or choose from Asset Library
                      </button>
                    </div>
                  )}
                </div>
                <p className="text-xs text-gray-500 mt-3">
                  PNG, JPG, or WebP up to 5MB
                </p>
                <p className="text-xs text-gray-500">
                  Recommended size: {getRecommendedDimensions()}
                </p>
              </div>
            </div>
          )}

          {/* Upload progress indicator */}
          {isUploading && (
            <div className="mt-4">
              <div className="flex items-center space-x-2">
                <div className="flex-1 bg-gray-200 rounded-full h-2 overflow-hidden">
                  <div className="bg-primary-600 h-full rounded-full animate-pulse w-full"></div>
                </div>
                <span className="text-sm text-gray-600">Uploading...</span>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Company Asset Library Modal - only show if companyId is provided */}
      {companyId && (
        <CompanyAssetLibrary
          isOpen={isAssetLibraryOpen}
          onClose={() => setIsAssetLibraryOpen(false)}
          onAssetSelected={handleAssetSelected}
          assetType={type === 'logo' ? 'logo' : 'image'}
        />
      )}
    </>
  );
}
